import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import { Empty, Period, SiteHead, eventStateLabel, formatDate } from '../../components/site';
import type { GameCard } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface EventItem {
  slug: string; name: string; kind: string; state: string; game_id: string | null;
  starts_at: string | null; ends_at: string | null; claim_deadline: string | null;
  eligibility: string;
}

interface EventDetailPayload extends EventItem {
  game: GameCard | null;
  conditions: string | null;
  rewards_summary: string | null;
  policy_note: string | null;
  web_claim_allowed: boolean;
}

const STATES = [
  { value: 'all', label: '전체' },
  { value: 'active', label: '진행 중' },
  { value: 'upcoming', label: '시작 전' },
  { value: 'ended', label: '종료' },
];

/** P006 — event list. Eligibility is never asserted to a signed-out visitor. */
export function PublicEvents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = searchParams.get('state') ?? 'all';

  const events = useResource<{ events: EventItem[]; note: string }>(
    (signal) => api.public('/api/v1/events', { signal, query: { state } }) as Promise<ApiEnvelope<{ events: EventItem[]; note: string }>>,
    [state],
    { emptyWhen: () => false },
  );

  const games = useResource<GameCard[]>(
    (signal) => api.public('/api/v1/games', { signal, query: { per_page: 60 } }) as Promise<ApiEnvelope<GameCard[]>>,
    [],
  );

  const gameName = (id: string | null) => games.data?.find((game) => game.id === id)?.name;

  return (
    <>
      <SiteHead
        eyebrow="이벤트"
        title="이벤트"
        lead="진행 중이거나 곧 시작하는 이벤트"
        meta={<span>보상 자격은 게임에 로그인한 뒤 확인할 수 있어요.</span>}
      />

      <div className="chips">
        {STATES.map((option) => (
          <button
            key={option.value}
            type="button"
            className="chip"
            aria-pressed={state === option.value}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (option.value === 'all') next.delete('state');
              else next.set('state', option.value);
              setSearchParams(next);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      <StateCard state={events.state} error={events.error} onRetry={events.reload}>
        {(events.data?.events.length ?? 0) === 0 ? (
          <Empty
            title={state === 'all' ? '등록된 이벤트가 없어요' : '이 조건에 맞는 이벤트가 없어요'}
            body={state === 'all' ? '새 이벤트가 열리면 여기에서 확인할 수 있어요.' : '전체 이벤트에서 다른 일정을 확인해 보세요.'}
            action={state !== 'all' ? (
              <button type="button" className="btn" onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('state');
                setSearchParams(next);
              }}>전체 이벤트 보기</button>
            ) : undefined}
          />
        ) : (
          <div className="card-grid">
            {events.data!.events.map((event) => {
              const stateInfo = eventStateLabel(event.state);
              return (
                <Link key={event.slug} to={`/events/${event.slug}`} className="event-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <h3 className="event-card__title">{event.name}</h3>
                    <Pill tone={stateInfo.tone}>{stateInfo.label}</Pill>
                  </div>
                  {event.game_id && (
                    <p className="event-card__reward">{gameName(event.game_id) ?? '게임'}</p>
                  )}
                  <Period startsAt={event.starts_at} endsAt={event.ends_at} />
                  {event.claim_deadline && (
                    <p className="event-card__reward">수령 마감 {formatDate(event.claim_deadline)}</p>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </StateCard>
    </>
  );
}

/** P007 — event detail. */
export function PublicEventDetail() {
  const { slug } = useParams();

  const event = useResource<EventDetailPayload>(
    (signal) => api.public(`/api/v1/events/${slug}`, { signal }) as Promise<ApiEnvelope<EventDetailPayload>>,
    [slug],
    { emptyWhen: () => false },
  );

  const data = event.data;
  const stateInfo = data ? eventStateLabel(data.state) : null;

  return (
    <StateCard state={event.state} error={event.error} onRetry={event.reload}>
      {data && stateInfo && (
        <article className="article">
          <nav className="breadcrumb" style={{ marginBottom: 20 }}>
            <Link to="/events">이벤트</Link> / {data.name}
          </nav>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <Pill tone={stateInfo.tone}>{stateInfo.label}</Pill>
            {data.game && <Pill tone="neutral">{data.game.name}</Pill>}
          </div>

          <h1 className="site-head__title">{data.name}</h1>

          <div className="card" style={{ marginBottom: 28 }}>
            <Period startsAt={data.starts_at} endsAt={data.ends_at} />
            <dl className="spec-list" style={{ marginTop: 16 }}>
              <dt>시작</dt><dd>{formatDate(data.starts_at, true)}</dd>
              <dt>종료</dt><dd>{formatDate(data.ends_at, true)}</dd>
              <dt>수령 마감</dt><dd>{data.claim_deadline ? formatDate(data.claim_deadline, true) : '별도 마감 없음'}</dd>
            </dl>
          </div>

          {data.conditions && (
            <section style={{ marginBottom: 28 }}>
              <h2 className="section__title" style={{ marginBottom: 8 }}>참여 조건</h2>
              <p style={{ margin: 0, lineHeight: 1.8 }}>{data.conditions}</p>
            </section>
          )}

          {data.rewards_summary && (
            <section style={{ marginBottom: 28 }}>
              <h2 className="section__title" style={{ marginBottom: 8 }}>보상</h2>
              <p style={{ margin: 0, lineHeight: 1.8 }}>{data.rewards_summary}</p>
            </section>
          )}

          <div className="banner banner--info">
            <span aria-hidden="true">ℹ</span>
            <div>
              {data.web_claim_allowed
                ? '웹에서 보상 수령 가능 · 로그인 후 자격 확인'
                : '보상은 게임 내 선물함에서 확인하세요.'}
            </div>
          </div>

          {data.policy_note && (
            <div className="article__notice" style={{ marginTop: 16 }}>{data.policy_note}</div>
          )}

          <div style={{ marginTop: 40, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link className="btn" to="/events">← 목록으로</Link>
            {data.game && <Link className="btn" to={`/games/${data.game.slug}`}>게임 보기</Link>}
            <Link className="btn" to="/account/sign-in">로그인</Link>
          </div>
        </article>
      )}
    </StateCard>
  );
}
